/**
 * The Competency contract — one flat router, `competencyContract` (`/competencies`), covering both
 * the competency catalogue and the review templates built from it. See the Performance Reviews
 * plan's "Contract & API" section — `tsRestRouter` rejects nesting, so this stays flat and separate
 * from `reviewCycleContract`/`reviewContract` rather than nesting templates under competencies.
 *
 * `setTemplateCompetencies`'S SUM-TO-100 RULE IS ENFORCED IN THE ROUTE, NOT A DB CHECK — a template
 * is edited incrementally (add one competency, save, add another) and a hard constraint would block
 * saving a legitimately in-progress edit. The route returns 422 naming the actual sum so the admin
 * UI's running-total editor can surface it inline (see the plan's Admin implementation section).
 *
 * A TEMPLATE IN USE IS FROZEN (perf remediation #17, T2.12). Once a non-draft review cycle uses a
 * template (as the cycle's template, or as the template of a review in it), its competency set and
 * weights and its question set can no longer change — every live review was scored, or is being
 * written, against them. `setTemplateCompetencies`/`setTemplateQuestions` answer 409 and the admin
 * copies it instead (`duplicateTemplate`, copy-on-write) and points the next cycle at the copy.
 * `inUseByLiveCycle` on the template tells the UI up front. Name, description and `isActive` stay
 * editable — they change no score.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { COMPETENCY_CATEGORIES, REVIEW_QUESTION_TYPES, REVIEW_QUESTION_REVIEWER_TYPES } from '../enums.js';
import {
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const competencyCategorySchema = z.enum(COMPETENCY_CATEGORIES);

// ---------------------------------------------------------------------------
// DTOs — competencies
// ---------------------------------------------------------------------------

export const competencySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: competencyCategorySchema,
  /** The mockup's "Req. Level 4" — keyed by GradeCatalogue.id (PRF-32; migrated off the earlier
   * free-text grade code), valued by the expected rating level on the cycle's scale. */
  expectedLevelByGrade: z.record(z.string(), z.number()),
  /** Server-resolved `GradeCatalogue.id -> name` for exactly the keys in `expectedLevelByGrade`
   * above — lets any reader (including a plain employee opening their own review's rubric, who
   * holds no `org.settings:read`) render grade names without a separate catalogue-browse call. */
  gradeNames: z.record(z.string(), z.string()),
  isActive: z.boolean(),
  createdAt: instantSchema,
});
export type Competency = z.infer<typeof competencySchema>;

/** Read-only id+name pairs for the grade picker on this module's own admin forms (Competency,
 *  Skill, Review Cycle target-score editors) — gated on OUR permissions
 *  (competency:manage/skill:manage/review.cycle:manage), never `org.settings:read`, which a
 *  performance_admin-only role (no hr_admin) does not hold. Read-only passthrough over
 *  `GradeCatalogue` — never mutated here, see the plan's Scope boundary ("we use but don't change
 *  ... the GradeCatalogue model"). */
export const gradeOptionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
});
export type GradeOption = z.infer<typeof gradeOptionSchema>;

export const listCompetenciesQuerySchema = paginationQuerySchema.extend({
  category: competencyCategorySchema.optional(),
  isActive: z.coerce.boolean().optional(),
});

export const createCompetencyBodySchema = z.object({
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  category: competencyCategorySchema,
  expectedLevelByGrade: z.record(z.string(), z.number()).optional(),
});

export const updateCompetencyBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  category: competencyCategorySchema.optional(),
  expectedLevelByGrade: z.record(z.string(), z.number()).optional(),
});

// ---------------------------------------------------------------------------
// DTOs — review templates
// ---------------------------------------------------------------------------

export const templateCompetencySchema = z.object({
  id: uuidSchema,
  competencyId: uuidSchema,
  competencyName: z.string(),
  competencyCategory: competencyCategorySchema,
  weightPercent: percentSchema,
  sortOrder: z.number().int(),
  requiresEvidence: z.boolean(),
});
export type TemplateCompetency = z.infer<typeof templateCompetencySchema>;

// ---------------------------------------------------------------------------
// DTOs — custom review questions (PRF-8), built on @hrms/custom-fields' generic field shape
// ---------------------------------------------------------------------------

export const templateQuestionTypeSchema = z.enum(REVIEW_QUESTION_TYPES);
export const templateQuestionReviewerTypeSchema = z.enum(REVIEW_QUESTION_REVIEWER_TYPES);

export const templateQuestionOptionSchema = z.object({
  value: z.string().min(1).max(100),
  label: z.string().min(1).max(200),
  sortOrder: z.number().int(),
});

export const templateQuestionSchema = z.object({
  id: uuidSchema,
  templateId: uuidSchema,
  type: templateQuestionTypeSchema,
  label: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  sortOrder: z.number().int(),
  options: z.array(templateQuestionOptionSchema).nullable(),
  scaleMax: z.number().int().nullable(),
  maxLength: z.number().int().nullable(),
  /** Null = informational, never scored. Rating questions only — see the route's own validation. */
  weightPercent: percentSchema.nullable(),
  reviewerTypes: z.array(templateQuestionReviewerTypeSchema),
  isActive: z.boolean(),
});
export type TemplateQuestion = z.infer<typeof templateQuestionSchema>;

export const reviewTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  competencies: z.array(templateCompetencySchema),
  questions: z.array(templateQuestionSchema),
  /** A non-draft review cycle uses this template: its competencies, weights and questions are
   * frozen (409 on change) — duplicate it to edit. Optional on the wire for older clients. */
  inUseByLiveCycle: z.boolean().optional(),
  createdAt: instantSchema,
});
export type ReviewTemplate = z.infer<typeof reviewTemplateSchema>;

export const listTemplatesQuerySchema = paginationQuerySchema.extend({
  isActive: z.coerce.boolean().optional(),
});

export const createTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
});

/** Copy-on-write for a template a live cycle has frozen: a new, editable template with the same
 * competencies, weights and active questions under a new (unique) name. */
export const duplicateTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
});
export type DuplicateTemplateBody = z.infer<typeof duplicateTemplateBodySchema>;

export const updateTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  isActive: z.boolean().optional(),
});

/** Replaces the template's whole competency set in one call — the admin UI's weights editor saves
 * the full running list at once, not one row at a time, so a mid-edit sum is never persisted. */
export const setTemplateCompetenciesBodySchema = z.object({
  competencies: z
    .array(
      z.object({
        competencyId: uuidSchema,
        weightPercent: percentSchema,
        sortOrder: z.number().int().min(0),
        requiresEvidence: z.boolean().optional(),
      }),
    )
    .max(30),
});

/** Replaces the template's whole question set in one call, same "send the full list" convention
 * `setTemplateCompetenciesBodySchema` uses. Unlike competencies, question weights are NOT
 * required to sum to 100 — a template can mix scored and purely-informational questions, and the
 * scored ones' weights are on their own separate axis from competency weights (the route documents
 * exactly how they blend into computeReviewScore). */
export const setTemplateQuestionsBodySchema = z.object({
  questions: z
    .array(
      z.object({
        /** Present = update this existing question in place; absent = create a new one. A
         * question with real answers cannot be dropped from the set (409, naming it) - see the
         * route for why deleting it would cascade-destroy those answers. */
        id: uuidSchema.optional(),
        type: templateQuestionTypeSchema,
        label: z.string().trim().min(1).max(300),
        description: z.string().trim().max(2000).optional(),
        required: z.boolean().optional(),
        sortOrder: z.number().int().min(0),
        options: z.array(templateQuestionOptionSchema).max(20).optional(),
        scaleMax: z.number().int().min(2).max(10).optional(),
        maxLength: z.number().int().min(1).max(4000).optional(),
        weightPercent: percentSchema.optional(),
        reviewerTypes: z.array(templateQuestionReviewerTypeSchema).min(1),
      }),
    )
    .max(50),
});

// ---------------------------------------------------------------------------
// Contract — /competencies
// ---------------------------------------------------------------------------

export const competencyContract = c.router(
  {
    listCompetencies: {
      method: 'GET',
      path: '/',
      query: listCompetenciesQuerySchema,
      responses: {
        200: paginatedSchema(competencySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List the competency catalogue',
      metadata: routeMeta('review:read'),
    },

    // Declared before `getCompetency` on purpose: Express matches in declaration order, so `/:competencyId`
    // would otherwise swallow GET /templates (see contract-route-order.test.ts).
    listTemplates: {
      method: 'GET',
      path: '/templates',
      query: listTemplatesQuerySchema,
      responses: {
        200: paginatedSchema(reviewTemplateSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List review templates',
      metadata: routeMeta('review:read'),
    },

    // Also declared before `getCompetency` — a static path, same ordering reason as `listTemplates`.
    listGradeOptions: {
      method: 'GET',
      path: '/grade-options',
      responses: {
        200: z.object({ items: z.array(gradeOptionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "The grade catalogue's id+name pairs, for this module's own admin pickers (PRF-32) — competency:manage/skill:manage/review.cycle:manage, any one",
      metadata: routeMeta(['competency:manage', 'skill:manage', 'review.cycle:manage']),
    },

    getCompetency: {
      method: 'GET',
      path: '/:competencyId',
      responses: {
        200: competencySchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one competency',
      metadata: routeMeta('review:read'),
    },

    createCompetency: {
      method: 'POST',
      path: '/',
      body: createCompetencyBodySchema,
      responses: {
        201: competencySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a competency — competency:manage',
      metadata: routeMeta('competency:manage'),
    },

    updateCompetency: {
      method: 'PATCH',
      path: '/:competencyId',
      body: updateCompetencyBodySchema,
      responses: {
        200: competencySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a competency — competency:manage',
      metadata: routeMeta('competency:manage'),
    },

    archiveCompetency: {
      method: 'POST',
      path: '/:competencyId/archive',
      body: z.object({}),
      responses: {
        200: competencySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Archive (deactivate) a competency — competency:manage',
      metadata: routeMeta('competency:manage'),
    },

    getTemplate: {
      method: 'GET',
      path: '/templates/:templateId',
      responses: {
        200: reviewTemplateSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one review template with its competency weights',
      metadata: routeMeta('review:read'),
    },

    createTemplate: {
      method: 'POST',
      path: '/templates',
      body: createTemplateBodySchema,
      responses: {
        201: reviewTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a review template — competency:manage',
      metadata: routeMeta('competency:manage'),
    },

    updateTemplate: {
      method: 'PATCH',
      path: '/templates/:templateId',
      body: updateTemplateBodySchema,
      responses: {
        200: reviewTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a review template — competency:manage',
      metadata: routeMeta('competency:manage'),
    },

    setTemplateCompetencies: {
      method: 'PUT',
      path: '/templates/:templateId/competencies',
      body: setTemplateCompetenciesBodySchema,
      responses: {
        200: reviewTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The template is in use by a non-draft cycle — duplicate it instead. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Replace the template's competency set and weights — 422 naming the actual sum when weights do not total 100; 409 while a non-draft cycle uses the template (duplicate it)",
      metadata: routeMeta('competency:manage'),
    },

    setTemplateQuestions: {
      method: 'PUT',
      path: '/templates/:templateId/questions',
      body: setTemplateQuestionsBodySchema,
      responses: {
        200: reviewTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The template is in use by a non-draft cycle, or an answered question was dropped. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Replace the template's custom question set (PRF-8) — competency:manage; 409 while a non-draft cycle uses the template (duplicate it)",
      metadata: routeMeta('competency:manage'),
    },

    duplicateTemplate: {
      method: 'POST',
      path: '/templates/:templateId/duplicate',
      body: duplicateTemplateBodySchema,
      responses: {
        201: reviewTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The name is taken. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Copy a review template (competencies, weights, active questions) under a new name — the way to edit a template a live cycle has frozen. competency:manage. Requires Idempotency-Key',
      metadata: routeMeta('competency:manage'),
    },
  },
  { pathPrefix: '/competencies' },
);
